import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRightLeft } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
	DIALOG_CANCEL_CLASS,
	DIALOG_CONFIRM_CLASS,
	DIALOG_CONTENT_CLASS,
	DIALOG_DESCRIPTION_CLASS,
	DIALOG_FOOTER_CLASS,
	DIALOG_HEADER_CLASS,
	DIALOG_TITLE_CLASS
} from '@/components/shared/dialog-styles';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { ToggleRow } from '@/modules/settings';
import { useBusinessUnit } from '@/hooks/use-business-units';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/utils';
import type { MigrationEntry } from '@/types/rules.types';
import { useAutopilotMigration, useMigrateToAutopilot } from '../hooks/use-ticket-rules';
import { useRollBackDialog } from '../hooks/use-roll-back-dialog';
import { PLACES } from '../lib/places';

const when = (iso: string): string =>
	new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const describeEntry = (entry: MigrationEntry): string =>
	`${entry.event === 'migrated' ? 'Playbooks enabled' : 'Playbooks disabled'} ${when(entry.at)}${entry.actorEmail ? ` by ${entry.actorEmail}` : ''}`;

/**
 * Brand Settings: enable playbooks for the selected brand, and disable them (a migration and a roll back under
 * the hood). Enabling previews the playbooks the
 * brand's current settings become; rolling back switches playbooks off, and the settings — never changed
 * by migrating — are in charge again. Administrators only; everyone else sees the state and history.
 */
export function AutopilotMigration() {
	const { can } = usePermissions();
	// Playbooks are part of Automation: without access to it there is nothing to show here.
	if (!can('view', 'automation')) return null;
	return <AutopilotMigrationRow />;
}

function AutopilotMigrationRow() {
	const navigate = useNavigate();
	const status = useAutopilotMigration();
	const migrate = useMigrateToAutopilot();
	const rollBack = useRollBackDialog();
	const { buId, businessUnits } = useBusinessUnit();
	const { can, role, isSuperAdmin } = usePermissions();
	const canMove = can('edit', 'brand') && (isSuperAdmin || role === 'administrator') && Boolean(buId);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const brandName = businessUnits.find((unit) => unit.id === buId)?.name ?? 'this brand';
	const data = status.data;
	const isOn = data?.enabled === true;
	// Switched on some other way (the control panel): playbooks run, but the old settings still act too.
	const needsMigrating = isOn && data?.ownsSettings !== true;
	const preview = data?.preview ?? [];
	const lastMove = data?.history[0];
	const busy = migrate.isPending || rollBack.pending;

	const confirmMigrate = async () => {
		try {
			const entry = await migrate.mutateAsync();
			const created = entry.conversion?.created.length ?? 0;
			toast.success(`Playbooks are on for ${brandName}${created ? `, with ${created} playbook${created === 1 ? '' : 's'} from your settings` : ''}`, {
				action: { label: 'See custom playbooks', onClick: () => navigate(PLACES['pre-processing'].path) }
			});
		} catch (error) {
			console.error('[AutopilotMigration.migrate] failed', { buId, error });
			// Without an answer the move may still have happened, so say what is known.
			const answered = typeof error === 'object' && error !== null && 'response' in error && Boolean((error as { response?: unknown }).response);
			toast.error(
				answered
					? getApiErrorMessage(error, "Couldn't enable playbooks for this brand. Nothing was changed. Try again.")
					: "Couldn't confirm playbooks were enabled. Reload to check whether they are on before trying again."
			);
		}
	};

	const description = status.isError
		? 'Could not load playbooks for this brand. Reload the page to try again.'
		: status.isLoading
			? 'Loading…'
			: needsMigrating
				? "On, but this brand's settings haven't been handed over yet, so they still apply alongside its playbooks. Finish enabling to hand them over, or switch playbooks off."
				: isOn
					? `On. This brand's playbooks run first, and anything complex goes to Advanced AI.${lastMove ? ` ${describeEntry(lastMove)}.` : ''} Switch playbooks off at any time to go back to the previous settings, exactly as they were.`
					: "Off. This brand follows Kim's current settings. Switching playbooks on turns those settings into playbooks that do exactly the same, and you can switch them off at any time.";

	return (
		<div className='flex flex-col gap-3'>
			<ToggleRow
				title='Playbooks (beta)'
				description={
					<>
						{description}{data && !status.isError && ' The switch applies to this brand only.'}
						{!canMove && ' Only administrators can switch playbooks on or off.'}
					</>
				}
				action={
					<div className='flex items-center gap-3'>
						{needsMigrating && (
							<PillButton variant='primary' onClick={() => setConfirmOpen(true)} disabled={!canMove || busy || status.isError}>
								<ArrowRightLeft className='size-3.5' aria-hidden />
								{migrate.isPending ? 'Finishing…' : 'Finish enabling'}
							</PillButton>
						)}
						{/* Same switch as Shadow mode; each way still asks first (enable previews the playbooks). */}
						<Switch
							aria-label={`Playbooks for ${brandName}`}
							checked={isOn}
							disabled={!canMove || busy || status.isLoading || status.isError}
							onCheckedChange={(next) => (next ? setConfirmOpen(true) : rollBack.open())}
						/>
					</div>
				}
			/>

			{data && data.history.length > 0 && (
				<details className='rounded-2xl border border-gray-300 bg-gray-50 px-4 py-2 text-[12px] text-gray-900'>
					<summary className='cursor-pointer font-medium text-ink'>History</summary>
					<ul className='mt-2 flex flex-col gap-1'>
						{data.history.slice(0, 10).map((entry) => (
							<li key={entry.at + entry.event}>{describeEntry(entry)}</li>
						))}
					</ul>
				</details>
			)}

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent className={DIALOG_CONTENT_CLASS}>
					<AlertDialogHeader className={DIALOG_HEADER_CLASS}>
						<ArrowRightLeft className='size-7 text-indigo-500' />
						<AlertDialogTitle className={DIALOG_TITLE_CLASS}>
							{needsMigrating ? 'Finish enabling' : 'Enable'} playbooks for {brandName}?
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className={DIALOG_DESCRIPTION_CLASS}>
								{preview.length > 0 ? (
									<>
										<p>These settings become playbooks that do exactly what they do today:</p>
										<ul className='mt-2 flex list-disc flex-col gap-1.5 pl-5 text-left'>
											{preview.map((item) => (
												<li key={item.systemKey}>
													<span className='font-medium text-ink'>{item.name}.</span> {item.description}
												</li>
											))}
										</ul>
									</>
								) : (
									<p>This brand has no filters, spam, tag or assignment settings to bring over, so it starts with no playbooks.</p>
								)}
								<p className='mt-3'>
									Your settings themselves are not changed. If anything isn&apos;t right, disable playbooks and Kim goes back to your settings exactly as
									they are now.
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className={DIALOG_FOOTER_CLASS}>
						<AlertDialogCancel className={DIALOG_CANCEL_CLASS}>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={() => void confirmMigrate()} className={DIALOG_CONFIRM_CLASS}>
							Enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			{rollBack.dialog}
		</div>
	);
}
